import { z } from 'zod';
import { isoDateTimeSchema, provinceSchema, uuidSchema } from './common.js';
/**
 * Sign-up, setup and billing.
 *
 * A firm or a business arrives on its own, answers what an accountant would
 * ask, and starts a thirty-day trial. A firm's account covers every client
 * business it adds; a business's covers itself. Each business has a monthly
 * ceiling on AI reading, so a bad batch cannot cost money unnoticed.
 */
export const ACCOUNT_KINDS = ['firm', 'business'];
export const accountKindSchema = z.enum(ACCOUNT_KINDS);
export const signUpRequestSchema = z.object({
    kind: accountKindSchema,
    /** The firm's name, or the business's. */
    accountName: z.string().trim().min(2, 'The name of the firm or business.').max(200),
    fullName: z.string().trim().min(2, 'Your name, as colleagues know it.').max(120),
    email: z.email('A valid email address.').max(254),
    password: z.string().min(10, 'At least ten characters.').max(200),
});
export const signUpResultSchema = z.object({
    /** True when a confirmation email was sent; false when the account can sign in now. */
    needsConfirmation: z.boolean(),
});
export const passwordResetRequestSchema = z.object({ email: z.email().max(254) });
export const startAccountRequestSchema = z
    .object({ kind: accountKindSchema, accountName: z.string().trim().min(2).max(200) })
    .partial();
export const TRADES = [
    'hvac',
    'plumbing',
    'electrical',
    'roofing',
    'general_contracting',
    'other',
];
export const tradeSchema = z.enum(TRADES);
export const TRADE_LABELS = {
    hvac: 'Heating, ventilation and air conditioning',
    plumbing: 'Plumbing',
    electrical: 'Electrical',
    roofing: 'Roofing',
    general_contracting: 'General contracting',
    other: 'Another trade',
};
export const FILING_FREQUENCIES = ['monthly', 'quarterly', 'annual'];
export const filingFrequencySchema = z.enum(FILING_FREQUENCIES);
export const completeSetupRequestSchema = z.object({
    province: provinceSchema.optional(),
    trade: tradeSchema.optional(),
    fiscalYearEndMonth: z.number().int().min(1).max(12).optional(),
    /** The GST/HST registration number, printed on invoices. Empty clears it. */
    businessNumber: z.string().trim().max(40).optional(),
    filingFrequency: filingFrequencySchema.optional(),
});
export const BILLING_STATUSES = [
    'trialing',
    'active',
    'past_due',
    'canceled',
    'complimentary',
];
export const billingStatusSchema = z.enum(BILLING_STATUSES);
export const billingOverviewSchema = z.object({
    account: z
        .object({
        id: uuidSchema,
        kind: accountKindSchema,
        name: z.string(),
        status: billingStatusSchema,
        trialEndsAt: isoDateTimeSchema.nullable(),
        currentPeriodEnd: isoDateTimeSchema.nullable(),
        hasPaymentMethod: z.boolean(),
        inGoodStanding: z.boolean(),
        businessesCovered: z.number().int(),
    })
        .nullable(),
    businesses: z.array(z.object({
        organizationId: uuidSchema,
        name: z.string(),
        coveredByYou: z.boolean(),
        spentMinor: z.string(),
        ceilingMinor: z.string(),
        canChangeCeiling: z.boolean(),
    })),
    /** Whether this server can take payment at all. */
    paymentsEnabled: z.boolean(),
});
export const redirectUrlSchema = z.object({ url: z.url() });
export const setAiCeilingRequestSchema = z.object({
    ceilingMinor: z.string().regex(/^\d{1,8}$/, 'A whole number of cents.'),
});
//# sourceMappingURL=billing.js.map